/**
 * Resize an image file to fit within `maxDimension` (longest side), preserving
 * aspect ratio, and return it as a Blob. Runs entirely in the browser via a
 * canvas — no server-side processing required.
 */
export async function resizeImageToBlob(
  file: File,
  maxDimension: number,
  mimeType = 'image/jpeg',
  quality = 0.8,
): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  try {
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D canvas context for image resizing.');
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('Failed to encode resized image.')),
        mimeType,
        quality,
      );
    });
  } finally {
    bitmap.close();
  }
}
